import React from 'react';
import ReactDOM from 'react-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import QuoteTemplate from '../features/Bookings/templates/QuoteTemplate';
import InvoiceTemplate from '../features/Bookings/templates/InvoiceTemplate';
import ConfirmationTemplate from '../features/Bookings/templates/ConfirmationTemplate';
import ReceiptTemplate from '../features/Bookings/templates/ReceiptTemplate';
import { s3Upload } from './awsLib';

/**
 * Generates a PDF for a given charter using React templates.
 * @param {object} options - Options for PDF generation.
 * @param {object} options.charter - The charter object.
 * @param {object} options.operator - The operator object.
 * @param {string} options.pdfType - The type of PDF to generate ('quote', 'invoice', 'confirmation', 'receipt').
 * @param {object} [options.allCharterSchedules] - All charter schedules (optional, for templates that need it).
 * @returns {Promise<File>} A promise that resolves with the generated PDF file.
 */
export const generateCharterPDF = async ({ charter, operator, pdfType, allCharterSchedules }) => {
    console.log(`[pdfGenerator] Generating ${pdfType} PDF for charter ${charter.charterId}`);

    const tempContainer = document.createElement('div');
    tempContainer.style.width = '210mm';
    tempContainer.style.background = 'white';
    tempContainer.style.boxSizing = 'border-box';
    tempContainer.style.fontFamily = 'Arial, sans-serif';
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '0';
    document.body.appendChild(tempContainer);

    let Template;
    let title;

    switch (pdfType) {
        case 'confirmation':
            Template = ConfirmationTemplate;
            title = 'Booking Confirmation';
            break;
        case 'invoice':
            Template = InvoiceTemplate;
            title = 'Tax Invoice';
            break;
        case 'receipt':
            Template = ReceiptTemplate;
            title = 'Payment Receipt';
            break;
        default:
            Template = QuoteTemplate;
            title = 'Charter Quote';
    }

    // Render the appropriate template
    ReactDOM.render(
        React.createElement(Template, {
            charter: charter,
            allCharterSchedules: allCharterSchedules, // Pass if needed by template
            operator: operator,
            bookings: true
        }),
        tempContainer
    );

    // Wait for content to render
    await new Promise(resolve => setTimeout(resolve, 100)); // Reduced from 1000ms to 100ms

    try {
        // Remove all images from the container to avoid CORS issues (if any)
        const images = tempContainer.getElementsByTagName('img');
        Array.from(images).forEach(img => {
            if (img.id !== 'operator-logo') { // Keep operator logo if it's handled
                img.remove();
            }
        });

        // A4 dimensions
        const pageWidth = 210; // mm
        const pageHeight = 297; // mm
        const margin = 10; // mm
        const contentWidth = pageWidth - (2 * margin);
        const contentHeight = pageHeight - (2 * margin);

        // html2canvas settings
        const canvasScale = 2;
        const pxPerMm = 3.78;

        // IMPORTANT: Collect break point info BEFORE removing from DOM
        const containerRect = tempContainer.getBoundingClientRect();

        // Find all major block elements that define natural break points
        // We want to split BETWEEN these elements, not through them
        const blockSelectors = [
            '.quote-header',
            '.card-main',
            '.break-page',
            '.charter-itinerary-item > div' // Individual itinerary blocks
        ];
        const blockElements = tempContainer.querySelectorAll(blockSelectors.join(', '));

        // Collect the bottom edge of each block element (natural break points are after blocks)
        const naturalBreakPoints = [];
        blockElements.forEach(element => {
            const rect = element.getBoundingClientRect();
            const relativeBottom = (rect.bottom - containerRect.top) * canvasScale;
            const relativeTop = (rect.top - containerRect.top) * canvasScale;
            naturalBreakPoints.push({
                position: relativeBottom,
                top: relativeTop,
                bottom: relativeBottom,
                height: relativeBottom - relativeTop,
                isExplicitBreak: element.classList.contains('break-page')
            });
        });

        // Sort by position
        naturalBreakPoints.sort((a, b) => a.position - b.position);

        const canvas = await html2canvas(tempContainer, {
            scale: canvasScale,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            width: tempContainer.scrollWidth,
            height: tempContainer.scrollHeight
        });

        ReactDOM.unmountComponentAtNode(tempContainer);
        document.body.removeChild(tempContainer);

        // Calculate scaling factor to fit content width
        const canvasWidthInMM = canvas.width / canvasScale / pxPerMm;
        const scale = contentWidth / canvasWidthInMM;

        // Calculate page height in canvas pixels
        const pageHeightInPx = (contentHeight / scale) * pxPerMm * canvasScale;

        // Create PDF with A4 size
        const pdf = new jsPDF('p', 'mm', 'a4', true);

        // Calculate split points
        const splitPoints = [];
        let currentY = 0;

        while (currentY < canvas.height) {
            let idealEndY = currentY + pageHeightInPx;
            let actualEndY = idealEndY;

            if (idealEndY >= canvas.height) {
                // Last page - just take the rest
                actualEndY = canvas.height;
            } else {
                // Find the best natural break point
                // Look for explicit break-page elements first
                const explicitBreak = naturalBreakPoints.find(bp =>
                    bp.isExplicitBreak && bp.position > currentY && bp.position <= idealEndY
                );

                if (explicitBreak) {
                    // Use explicit break point
                    actualEndY = explicitBreak.position;
                    console.log(`Using explicit break-page at ${actualEndY}`);
                } else {
                    // Find the nearest natural break point that's before idealEndY
                    // but not too close to currentY (at least 20% of page height)
                    const minPageContent = currentY + (pageHeightInPx * 0.2);

                    // Find all valid break points for this page
                    const validBreakPoints = naturalBreakPoints.filter(bp =>
                        bp.position > minPageContent && bp.position <= idealEndY
                    );

                    if (validBreakPoints.length > 0) {
                        // Use the last valid break point (closest to ideal end)
                        actualEndY = validBreakPoints[validBreakPoints.length - 1].position;
                        console.log(`Found natural break point at ${actualEndY} (ideal was ${idealEndY})`);
                    } else {
                        // No good break point found - check if we'd cut through a block
                        const cuttingThrough = naturalBreakPoints.find(bp =>
                            idealEndY > bp.top && idealEndY < bp.bottom
                        );

                        if (cuttingThrough) {
                            // Would cut through an element - move break to just before it
                            if (cuttingThrough.top > minPageContent) {
                                actualEndY = cuttingThrough.top;
                                console.log(`Avoiding cut through element, moving break to ${actualEndY}`);
                            } else {
                                // Element starts too close to page top, break after it instead
                                actualEndY = cuttingThrough.bottom;
                                console.log(`Element too close to top, breaking after at ${actualEndY}`);
                            }
                        }
                        // else: no element being cut, use idealEndY
                    }
                }
            }

            splitPoints.push({
                startY: currentY,
                endY: actualEndY,
                height: actualEndY - currentY
            });

            currentY = actualEndY;
        }

        console.log('Split points:', splitPoints);

        // Generate each page
        for (let i = 0; i < splitPoints.length; i++) {
            if (i > 0) {
                pdf.addPage();
            }

            const split = splitPoints[i];

            // Create a canvas for this page slice
            const pageCanvas = document.createElement('canvas');
            const pageCtx = pageCanvas.getContext('2d');
            pageCanvas.width = canvas.width;
            pageCanvas.height = split.height;

            // Draw the slice from the main canvas
            pageCtx.drawImage(
                canvas,
                0, split.startY, canvas.width, split.height,
                0, 0, canvas.width, split.height
            );

            const imgData = pageCanvas.toDataURL('image/png', 1.0);

            // Calculate height in mm for this slice
            const sliceHeightInMM = (split.height / canvasScale / pxPerMm) * scale;

            pdf.addImage(
                imgData,
                'PNG',
                margin,
                margin,
                contentWidth,
                sliceHeightInMM
            );
        }

        const pdfBlob = pdf.output('blob');
        const pdfFile = new File([pdfBlob], `${title}.pdf`, { type: 'application/pdf' });
        return pdfFile;

    } catch (error) {
        console.error('Error generating PDF:', error);
        ReactDOM.unmountComponentAtNode(tempContainer);
        document.body.removeChild(tempContainer);
        throw error;
    }
};

/**
 * Generate and store PDFs for a charter
 * @param {Object} charter - Charter object
 * @param {Object} operator - Operator object
 */
export const generateAndStoreCharterPDFs = async (charter, operator) => {
    try {
        console.log(`[generateAndStoreCharterPDFs] Charter ${charter.charterId} - Status: ${charter.status}, QuoteStatus: ${charter.quoteStatus}`);
        
        // Import S3 upload function
        // const { s3Upload } = await import('../libs/awsLib');
        
        // Generate all PDFs regardless of status - operators might need any of them
        await Promise.all([
            generateAndUploadCharterPDF(charter, operator, 'quote', s3Upload),
            generateAndUploadCharterPDF(charter, operator, 'invoice', s3Upload),
            generateAndUploadCharterPDF(charter, operator, 'confirmation', s3Upload),
            generateAndUploadCharterPDF(charter, operator, 'receipt', s3Upload)
        ]);
        
    } catch (error) {
        console.error(`Error generating PDF for charter ${charter.charterId}:`, error);
        // Don't throw - PDF generation failure shouldn't break charter saving
    }
};

/**
 * Generate a single PDF for a charter and upload to S3
 * @param {Object} charter - Charter object
 * @param {Object} operator - Operator object
 * @param {string} pdfType - Type of PDF to generate
 * @param {Function} s3Upload - S3 upload function
 */
const generateAndUploadCharterPDF = async (charter, operator, pdfType, s3Upload) => {
    try {
        console.log(`[generateAndUploadCharterPDF] Generating ${pdfType} PDF for charter ${charter.charterId}`);

        // Use the reusable PDF generation utility
        const pdfFile = await generateCharterPDF({
            charter: charter,
            operator: operator,
            pdfType: pdfType
        });

        // Upload to S3
        const pdfKey = `${operator.operatorId}/charters/${charter.charterId}/${pdfType}.pdf`;
        await s3Upload(pdfFile, pdfKey, { level: 'private' });

        console.log(`Generated and stored ${pdfType} PDF for charter ${charter.charterId}`);

    } catch (error) {
        console.error(`Error generating PDF for charter ${charter.charterId}:`, error);
        throw error;
    }
};